import React, { useState, useEffect, useRef } from 'react';
import { X, Star, MessageSquare, Calendar, Save, Loader2, AlertCircle } from 'lucide-react';
import { useCustomerReviews } from '../context/CustomerReviewContext';

const ReviewModal = ({
  isOpen,
  onClose,
  order,
  onReviewSubmitted = null
}) => {
  const modalRef = useRef(null);
  const { createReview, loading, error, clearError, getReview, fetchOrderReview, areReviewsLoaded } = useCustomerReviews();

  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Get existing review for this order
  const existingReview = order ? getReview(order.id) : null;
  const isReadOnly = !!existingReview;

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
        }
      }, 100);
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isSubmitting]);

  // Initialize form when modal opens
  useEffect(() => {
    const initializeModal = async () => {
      if (isOpen && order) {
        console.log("ReviewModal opened for order:", order.id);

        // Fetch reviews if not already loaded
        if (!areReviewsLoaded(order.id)) {
          console.log("Reviews not loaded for order, fetching...", order.id);
          try {
            await fetchOrderReview(order.id);
          } catch (error) {
            console.error('Failed to fetch reviews in modal:', error);
          }
        }

        // Get existing review after potential fetch
        const currentReview = getReview(order.id);
        console.log("Current review for order:", order.id, currentReview);

        if (currentReview) {
          // Show existing review
          setRating(currentReview.rating || 0);
          setComment(currentReview.comment || currentReview.description || currentReview.text || '');
        } else {
          // Reset form for new review
          setRating(0);
          setComment('');
        }

        setSubmitSuccess(false);
        setIsSubmitting(false);
        clearError();
      }
    };

    initializeModal();
  }, [isOpen, order, clearError, fetchOrderReview, getReview, areReviewsLoaded]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStarClick = (starRating) => {
    if (!isReadOnly && !isSubmitting) {
      setRating(starRating);
    }
  };

  const handleStarHover = (starRating) => {
    if (!isReadOnly && !isSubmitting) {
      setHoveredRating(starRating);
    }
  };

  const handleStarLeave = () => {
    if (!isReadOnly && !isSubmitting) {
      setHoveredRating(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Only allow submission for new reviews
    if (isReadOnly || isSubmitting) {
      return;
    }

    if (rating === 0) {
      alert('Please select a rating');
      return;
    }

    if (comment.trim().length < 10) {
      alert('Please write at least 10 characters in your review');
      return;
    }

    setIsSubmitting(true);

    try {
      const reviewData = {
        rating,
        comment: comment.trim()
      };

      console.log('Submitting review for order:', order.id, reviewData);

      const result = await createReview(order.id, reviewData);

      if (result.success) {
        console.log('Review submitted successfully:', result);
        setSubmitSuccess(true);

        // Show success message briefly before closing
        setTimeout(() => {
          if (onReviewSubmitted) {
            onReviewSubmitted(order.id);
          }
          onClose();
        }, 1500);
      } else {
        console.error('Review submission failed:', result.error);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
      clearError();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  if (!isOpen || !order) return null;

  const displayRating = hoveredRating || rating;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 transition-opacity duration-300"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-modal-title"
        tabIndex={-1}
      >
        <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white shadow-2xl rounded-2xl transform transition-all duration-300 scale-100">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-teal-50 to-orange-50">
            <div>
              <h2 id="review-modal-title" className="text-2xl font-bold text-gray-900">
                {isReadOnly ? 'Your Review' : 'Write Review'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Order {order.orderNumber} • {order.items?.length || 0} item{order.items?.length !== 1 ? 's' : ''}
              </p>
              {submitSuccess && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-green-100 px-3 py-1 rounded-full">
                  <Save className="w-4 h-4" />
                  Review submitted successfully!
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                disabled={isSubmitting}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="p-6">
              {/* Order Items Preview */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Order Items</h3>
                <div className="flex flex-wrap gap-3">
                  {order.items?.slice(0, 4).map((item, index) => (
                    <div key={item.id} className="relative">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                        loading="lazy"
                      />
                      {item.quantity > 1 && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-teal-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {item.quantity}
                        </div>
                      )}
                    </div>
                  ))}
                  {order.items?.length > 4 && (
                    <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-600">
                        +{order.items.length - 4}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Review Form/Display */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Rating */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    {isReadOnly ? 'Your Rating' : 'Rate your experience'}
                  </label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleStarClick(star)}
                        onMouseEnter={() => handleStarHover(star)}
                        onMouseLeave={handleStarLeave}
                        disabled={isReadOnly || isSubmitting}
                        className={`p-1 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded ${
                          !isReadOnly && !isSubmitting ? 'hover:scale-110 cursor-pointer' : 'cursor-default'
                        } ${isSubmitting ? 'opacity-50' : ''}`}
                      >
                        <Star
                          className={`w-8 h-8 transition-colors ${
                            star <= displayRating
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                    <span className="ml-3 text-sm text-gray-600">
                      {displayRating > 0 && (
                        <>
                          {displayRating} star{displayRating !== 1 ? 's' : ''}
                          {displayRating === 1 && ' - Poor'}
                          {displayRating === 2 && ' - Fair'}
                          {displayRating === 3 && ' - Good'}
                          {displayRating === 4 && ' - Very Good'}
                          {displayRating === 5 && ' - Excellent'}
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {/* Comment */}
                <div>
                  <label htmlFor="review-comment" className="block text-sm font-medium text-gray-700 mb-2">
                    {isReadOnly ? 'Your Review' : 'Write your review'}
                  </label>
                  {isReadOnly ? (
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-gray-700 leading-relaxed">{comment}</p>
                          {existingReview && existingReview.createdAt && (
                            <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
                              <Calendar className="w-4 h-4" />
                              <span>
                                Written {formatDate(existingReview.createdAt)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <textarea
                      id="review-comment"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Share your experience with this order. What did you like? What could be improved?"
                      rows={6}
                      disabled={isSubmitting}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 resize-none ${
                        isSubmitting ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''
                      }`}
                      required
                      minLength={10}
                    />
                  )}
                  {!isReadOnly && (
                    <p className="text-sm text-gray-500 mt-2">
                      Minimum 10 characters ({comment.length}/10)
                    </p>
                  )}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {/* Success Message */}
                {submitSuccess && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-800 text-sm font-medium mb-1">
                      <Save className="w-4 h-4" />
                      Review Submitted Successfully!
                    </div>
                    <p className="text-sm text-green-700">
                      Thank you for your feedback. The page will refresh to show your updated review status.
                    </p>
                  </div>
                )}

                {/* Action Buttons - Only show for new reviews */}
                {!isReadOnly && (
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || rating === 0 || comment.trim().length < 10 || submitSuccess}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Submitting...
                        </>
                      ) : submitSuccess ? (
                        <>
                          <Save className="w-4 h-4" />
                          Submitted!
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Submit Review
                        </>
                      )}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReviewModal;
